/**
 * Bifrost Auth Helper
 * 
 * Centralized authentication utility for the ZAPS frontend.
 * 
 * Token flow:
 *   - Bifrost/Cognito token is used for ALL API calls directly.
 *   - On localhost: DEV_TOKEN (hardcoded staging Cognito token) is used.
 *   - On deployed: Bifrost SDK provides the token via Cognito session.
 * 
 * getAuthToken() is SYNCHRONOUS — returns the Bifrost token.
 * All API utilities (fetchData.js, VendorApiUtils.js, PaymentRequestApiUtils.js)
 * use getAuthToken() for the Authorization header.
 */

import Bifrost from '@techzolo/bifrost-authenticate';

// ---- Dev Token for Local Development ----
// Paste your staging Bifrost access token here for local dev.
// Get a fresh token from staging Bifrost and update when it expires (~1 hour).
// Set to null to use the normal Bifrost login flow.
const DEV_TOKEN = process.env.REACT_APP_DEV_TOKEN || null;

/**
 * Check if a dev token is configured for local development.
 * Only active on localhost — bypasses the Bifrost login flow entirely.
 * @returns {string|null} The dev token or null
 */
export const getDevToken = () => {
  const hostname = window.location.hostname;
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && DEV_TOKEN) {
    return DEV_TOKEN;
  }
  return null;
};

/**
 * Detect the Bifrost environment from the app URL.
 * Maps React app URLs to Bifrost environment names.
 */
export const detectBifrostEnv = () => {
  const envOverride = process.env.REACT_APP_BIFROST_ENV;
  if (envOverride) return envOverride;

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'staging';
  if (hostname.includes('dev.zolostays.com')) return 'development';
  if (hostname.includes('stage.zolostays.com')) return 'staging';
  if (hostname.includes('preprod.zolostays.com')) return 'preprod';
  if (hostname.includes('zolostays.com')) return 'prod';
  return 'staging';
};

/**
 * Initialize Bifrost SDK.
 * Must be called once before any other auth operations.
 */
export const initializeBifrost = () => {
  const env = detectBifrostEnv();
  Bifrost.initialize(env);
  return env;
};

// ---- Cached Bifrost token (for synchronous access) ----
// Bifrost SDK only has getAccessToken() (async), but all API utilities
// need the token synchronously. BifrostAuthGate calls setCachedBifrostToken()
// after the async getAccessToken() resolves, so getAuthToken() can return it.
let cachedBifrostToken = null;

/**
 * Store the Bifrost token for synchronous access by API utilities.
 * Called by BifrostAuthGate after successful authentication.
 * @param {string} token - The Bifrost/Cognito access token
 */
export const setCachedBifrostToken = (token) => {
  cachedBifrostToken = token;
};

/**
 * Get the auth token (SYNCHRONOUS).
 * Returns the app JWT issued by /api/v1/user/me (Zotag pattern).
 * Falls back to DEV_TOKEN for the initial /me call on localhost.
 * @returns {string|null}
 */
export const getAuthToken = () => {
  // Prefer the app JWT (set by BifrostAuthGate after /me exchange)
  if (cachedBifrostToken) {
    return cachedBifrostToken;
  }
  // Fallback: DEV_TOKEN for initial /me call on localhost
  const hostname = window.location.hostname;
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && DEV_TOKEN) {
    return DEV_TOKEN;
  }
  return null;
};

/**
 * Get auth headers for API calls (SYNCHRONOUS).
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

/**
 * Check if user is currently authenticated (SYNCHRONOUS).
 * Works by checking if BifrostAuthGate has populated the userdata object.
 * BifrostAuthGate calls populateUserData() BEFORE rendering children,
 * so this is always accurate when components mount.
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const userdata = require('../config/jwt_token').default;
  return !!(userdata && userdata.role);
};

// ---- Environment Maps ----

const COOKIE_DOMAINS = {
  local: 'localhost',
  development: '.dev.zolostays.com',
  staging: '.stage.zolostays.com',
  preprod: '.preprod.zolostays.com',
  prod: '.zolostays.com',
};

const BIFROST_LOGIN_SERVICE = {
  local: 'http://localhost:3433',
  development: 'https://login.dev.zolostays.com',
  staging: 'https://login.stage.zolostays.com',
  preprod: 'https://login.preprod.zolostays.com',
  prod: 'https://login.zolostays.com',
};

// ---- Clear Bifrost Session ----

/**
 * Clear Cognito/Bifrost cookies and localStorage keys.
 * Used on the Access Denied page so that clicking Retry/Login Again
 * shows a fresh Bifrost login window instead of auto-logging in
 * with the same unauthorized account.
 */
export const clearBifrostSession = () => {
  // Clear Cognito localStorage keys
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('CognitoIdentityServiceProvider')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));

  // Clear all cookies on the current domain
  const env = detectBifrostEnv();
  const cookieDomain = COOKIE_DOMAINS[env] || COOKIE_DOMAINS.staging;

  document.cookie.split(';').forEach(function(c) {
    var name = c.split('=')[0].trim();
    if (!name) return;
    document.cookie = name + '=;Path=/;Expires=Thu, 01 Jan 1970 00:00:00 UTC';
    document.cookie = name + '=;Path=/;Domain=' + cookieDomain + ';Expires=Thu, 01 Jan 1970 00:00:00 UTC';
  });
};

// ---- Silent Bifrost Logout ----

/**
 * Clear the Bifrost/Cognito server-side session via hidden iframe.
 * User stays on the current page — no visible redirect.
 */
function silentBifrostLogout() {
  return new Promise((resolve) => {
    const env = detectBifrostEnv();
    const baseUrl = BIFROST_LOGIN_SERVICE[env] || BIFROST_LOGIN_SERVICE.staging;
    const logoutUrl = `${baseUrl}/logout?zolo_redirect=${encodeURIComponent(window.location.origin + '/home')}&return=true`;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch (e) { /* already removed */ }
    };

    const timer = setTimeout(() => { cleanup(); resolve(); }, 4000);
    iframe.addEventListener('load', () => { clearTimeout(timer); cleanup(); resolve(); });

    iframe.src = logoutUrl;
    document.body.appendChild(iframe);
  });
}

// ---- Logout (Zotag Pattern) ----

/**
 * Handle logout:
 * 1. Clear app-level state (userdata, legacy keys)
 * 2. Clear Cognito localStorage keys
 * 3. Clear Cognito/Bifrost cookies
 * 4. Set just-logged-out guard flag
 * 5. Silent iframe logout
 * 6. Redirect to ZAPS origin
 *
 * IMPORTANT: Never calls Bifrost.signOut() — it triggers Cognito's
 * redirectSignOut for Google/federated users, causing uncontrolled redirects.
 */
export const handleLogout = async () => {
  // 1. Clear persistent state (localStorage keys)
  // NOTE: Do NOT call clearUserData() here — it mutates the shared in-memory
  // userdata object, causing mounted components to crash before the redirect
  // happens. The in-memory state is gone on page reload anyway.
  localStorage.removeItem('jwt_token_zaps');
  localStorage.removeItem('selected_city');
  localStorage.removeItem('selected_role');

  // 2. Clear Cognito localStorage keys (Amplify stores tokens here)
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('CognitoIdentityServiceProvider')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));

  // 3. Clear ALL cookies on the current domain
  // Clearing only named cookies (CognitoIdentityServiceProvider, bifrost, etc.)
  // is insufficient — accumulated cookies from other TechZolo apps on
  // .stage.zolostays.com cause HTTP 431 (Request Headers Too Large) on the
  // Bifrost login server.
  const env = detectBifrostEnv();
  const cookieDomain = COOKIE_DOMAINS[env] || COOKIE_DOMAINS.staging;

  document.cookie.split(';').forEach(function(c) {
    var name = c.split('=')[0].trim();
    if (!name) return;
    // Clear on current path
    document.cookie = name + '=;Path=/;Expires=Thu, 01 Jan 1970 00:00:00 UTC';
    // Clear on domain (for cookies set with Domain=.stage.zolostays.com etc.)
    document.cookie = name + '=;Path=/;Domain=' + cookieDomain + ';Expires=Thu, 01 Jan 1970 00:00:00 UTC';
  });

  // 4. Set just-logged-out guard (prevents auto-login on next mount)
  sessionStorage.setItem('zaps_just_logged_out', '1');

  // 5. Redirect to ZAPS origin IMMEDIATELY (before silent logout)
  // Must redirect before React re-renders — clearUserData() already emptied
  // the userdata object, so components would crash accessing role/city/etc.
  // Silent iframe logout fires in background; even if it doesn't complete,
  // local tokens/cookies are already cleared above.
  silentBifrostLogout().catch(function() { /* ignore */ });
  window.location.href = window.location.origin;
};

/**
 * Redirect to Bifrost login
 */
export const redirectToLogin = () => {
  const hostname = window.location.hostname;
  const devToken = getDevToken();
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && devToken) {
    // On localhost with manual token: just reload — BifrostAuthGate will pick up DEV_TOKEN
    window.location.href = window.location.origin + '/home';
    return;
  }

  try {
    Bifrost.loginRedirect(window.location.origin + '/home');
  } catch (err) {
    console.error('Login redirect failed:', err);
    window.location.href = '/';
  }
};



// WEBPACK FOOTER //
// ./src/utils/authHelper.js